import styled, { css } from "styled-components";
import { Search } from "react-feather";
import { IconButton } from "../../components/atoms/IconButton";
import {
  fontSizes,
  lineHeights,
  typographyMixin,
} from "../../design-system/tokens/typography";
import { SecondarySurface } from "./DataStudioPage.styles";

export const Panel = styled(SecondarySurface)`
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const PanelHeading = styled.h2`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.base[1]};
  margin: 0;
  padding: ${({ theme }) => theme.spacing.base[2]}
    ${({ theme }) => theme.spacing.base[3]};
  border-bottom: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  white-space: nowrap;
`;

export const HeadingLabel = styled.span`
  ${typographyMixin("subtitle", 3)}
  line-height: ${lineHeights["30"]};
`;

export const HeadingCount = styled.span`
  ${typographyMixin("subtitle", 4)}
  line-height: ${lineHeights["30"]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SearchSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
  padding: ${({ theme }) => theme.spacing.base[2]}
    ${({ theme }) => theme.spacing.base[3]};
  border-bottom: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
`;

export const SearchField = styled.label`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.base[2]}
    ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.background.primary};
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.lg};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

export const ToolbarIconButton = styled(IconButton).attrs({
  size: "md",
  iconSize: 16,
})`
  width: ${({ theme }) => theme.spacing.base[9]};
  height: ${({ theme }) => theme.spacing.base[9]};
  background: ${({ theme }) => theme.colors.background.primary};
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: ${({ theme }) => theme.spacing.base[0]};
  }
`;

export const ToolbarMenuAnchor = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
`;

export const SearchIcon = styled(Search)`
  flex: 0 0 auto;
  width: ${({ theme }) => theme.spacing.base[4]};
  height: ${({ theme }) => theme.spacing.base[4]};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

export const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`;

export const TableList = styled.ul`
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TableNavigation = styled.nav`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
`;

export const TableListItem = styled.li`
  margin: 0;
  padding: 0;
`;

export const TableButton = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[3]};
  width: 100%;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.base[2]}
    ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  background: transparent;
  border: 0;
  border-right: calc(
      ${({ theme }) => theme.borders.width[2]} +
        ${({ theme }) => theme.borders.width[1]}
    )
    ${({ theme }) => theme.borders.style.solid} transparent;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  cursor: pointer;

  ${({ $selected }) =>
    $selected &&
    css`
      background: ${({ theme }) => theme.colors.background.tertiary};
      border-right-color: ${({ theme }) => theme.colors.primary[500]};
      ${typographyMixin("body", 3)}
    `}

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: calc(-1 * ${({ theme }) => theme.borders.width[2]});
  }
`;

export const SourceLogo = styled.img`
  flex: 0 0 auto;
  width: ${fontSizes["body-4"]};
  height: ${fontSizes["body-4"]};
  object-fit: contain;
`;

export const TableName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EmptyMessage = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.base[4]}
    ${({ theme }) => theme.spacing.base[3]};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;
